import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobsService, type Job, type JobStatus } from '../services/jobs.service';

const COLUMNS: { status: JobStatus; label: string; icon: string }[] = [
  { status: 'APPLIED', label: 'Contacted', icon: 'send' },
  { status: 'HR', label: 'HR Interview', icon: 'record_voice_over' },
  { status: 'TECH', label: 'Next Instances', icon: 'terminal' },
  { status: 'OFFER', label: 'Offer', icon: 'workspace_premium' },
  { status: 'REJECTED', label: 'Rejected', icon: 'cancel' },
];

const ICON_COLORS = ['text-primary', 'text-tertiary', 'text-secondary'];
function getIconColor(company: string) {
  return ICON_COLORS[company.charCodeAt(0) % ICON_COLORS.length];
}

function AddJobModal({
  onClose,
  onContinue,
  existingJobs,
}: {
  onClose: () => void;
  onContinue: (jobDescription: string) => void;
  existingJobs: Job[];
}) {
  const [jobDescription, setJobDescription] = useState('');
  const [duplicate, setDuplicate] = useState<Job | null>(null);

  const findDuplicate = (text: string): Job | null => {
    // 1. URL match (most precise)
    const urls = text.match(/https?:\/\/[^\s]+/g) ?? [];
    const normalizeUrl = (u: string) => u.replace(/\/+$/, '').toLowerCase();
    const urlMatch = existingJobs.find(
      (j) => j.jobUrl && urls.some((u) => normalizeUrl(u) === normalizeUrl(j.jobUrl!)),
    );
    if (urlMatch) return urlMatch;

    // 2. Company name match (word-boundary, case-insensitive)
    return (
      existingJobs.find((j) => {
        const company = j.company.trim();
        if (company.length < 3) return false;
        const escaped = company.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`\\b${escaped}\\b`, 'i').test(text);
      }) ?? null
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = jobDescription.trim();
    const dupe = findDuplicate(text);
    if (dupe) {
      setDuplicate(dupe);
    } else {
      onContinue(text);
    }
  };

  if (duplicate) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-surface-container rounded-xl p-6 w-full max-w-md border border-outline-variant/20 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="material-symbols-outlined text-base text-on-surface-variant">warning</span>
            <h2 className="text-base font-bold text-on-surface">Duplicate Application</h2>
          </div>
          <p className="text-sm text-on-surface-variant mb-3">
            It looks like you already have an application for this position:
          </p>
          <div className="bg-surface-container-high rounded-lg p-3 mb-6 border border-outline-variant/10">
            <p className="font-semibold text-sm text-on-surface">{duplicate.company}</p>
            <p className="text-xs text-on-surface-variant mt-0.5">{duplicate.role}</p>
            <p className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant/60 mt-2">
              {COLUMNS.find((c) => c.status === duplicate.status)?.label ?? duplicate.status}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setDuplicate(null)}
              className="flex-1 px-4 py-2.5 rounded-md bg-surface-container-high text-on-surface font-medium text-sm hover:bg-surface-bright transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => onContinue(jobDescription.trim())}
              className="flex-1 px-4 py-2.5 rounded-md primary-gradient text-on-primary font-semibold text-sm"
            >
              Add Anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-container rounded-xl p-6 w-full max-w-2xl border border-outline-variant/20 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-on-surface">Add Application</h2>
          <button onClick={onClose} className="text-on-surface-variant hover:text-on-surface transition-colors">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Job Description *">
            <textarea
              required
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full JD here. We'll send it to the AI assistant and create the application from there."
              rows={12}
              className="input-field resize-none"
            />
          </Field>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-md bg-surface-container-high text-on-surface font-medium text-sm hover:bg-surface-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!jobDescription.trim()}
              className="flex-1 px-4 py-2.5 rounded-md primary-gradient text-on-primary font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function JobCard({
  job,
  onStatusChange,
  onDelete,
}: {
  job: Job;
  onStatusChange: (id: string, status: JobStatus) => void;
  onDelete: (id: string) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const nextStatuses = COLUMNS.filter((c) => c.status !== job.status).map((c) => c.status);
  const isOffer = job.status === 'OFFER';
  const isRejected = job.status === 'REJECTED';
  const iconColor = getIconColor(job.company);

  const cardBg = isOffer
    ? 'bg-surface-container-high'
    : isRejected
    ? 'bg-surface-container-low'
    : 'bg-surface-container hover:bg-surface-container-high';

  const cardBorder = isOffer
    ? 'border border-primary/30'
    : job.status === 'HR'
    ? 'border-l-2 border-primary border-t border-r border-b border-transparent'
    : 'border border-transparent hover:border-outline-variant/10';

  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData('jobId', job.id)}
      onClick={() => navigate(`/jobs/${job.id}`)}
      className={`shrink-0 p-4 rounded-xl group cursor-pointer transition-all relative overflow-hidden ${cardBg} ${cardBorder}`}
    >
      {isOffer && (
        <div className="absolute top-0 right-0 w-16 h-16 -mr-8 -mt-8 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
      )}

      <div className="flex justify-between items-start mb-3">
        <div
          className={`w-10 h-10 rounded-lg ${isOffer ? 'bg-primary/20' : 'bg-surface-container-highest'} flex items-center justify-center`}
        >
          <span
            className={`material-symbols-outlined ${isOffer ? 'text-primary' : iconColor}`}
            style={isOffer ? { fontVariationSettings: "'FILL' 1" } : undefined}
          >
            {COLUMNS.find((c) => c.status === job.status)?.icon ?? 'work'}
          </span>
        </div>

        <div className="relative">
          {isOffer ? (
            <span className="material-symbols-outlined text-primary">celebration</span>
          ) : (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu((p) => !p); }}
                className="text-on-surface-variant opacity-0 group-hover:opacity-100 transition-opacity hover:text-on-surface"
              >
                <span className="material-symbols-outlined text-lg">drag_indicator</span>
              </button>
              {showMenu && (
                <div className="absolute right-0 top-6 bg-surface-container-high rounded-lg shadow-xl border border-outline-variant/20 py-1 z-20 min-w-36">
                  {nextStatuses.map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); onStatusChange(job.id, s); setShowMenu(false); }}
                      className="w-full text-left px-4 py-2 text-xs text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors"
                    >
                      Move to {COLUMNS.find((c) => c.status === s)?.label}
                    </button>
                  ))}
                  <div className="border-t border-outline-variant/10 my-1" />
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(job.id); setShowMenu(false); }}
                    className="w-full text-left px-4 py-2 text-xs text-error hover:bg-surface-container transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <h4 className="font-semibold text-sm text-on-surface">{job.company}</h4>
      <p className="text-on-surface-variant text-xs mb-3">{job.role}</p>

      {isOffer && (
        <p className="text-xs font-bold text-primary mb-2">Offer Received • Review Now</p>
      )}

      {job.notes && !isOffer && (
        <p className="text-xs text-on-surface-variant/80 bg-surface-container-highest/30 p-2 rounded italic whitespace-pre-wrap break-words">
          "{job.notes}"
        </p>
      )}

      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-outline-variant/5">
        <div className="flex items-center gap-1 text-[10px] text-on-surface-variant uppercase tracking-wider font-semibold">
          <span className="material-symbols-outlined text-xs">calendar_today</span>
          {new Date(job.appliedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
        {job.notes && (
          <div className="flex items-center gap-1 text-[10px] text-on-surface-variant uppercase tracking-wider font-semibold">
            <span className="material-symbols-outlined text-xs">notes</span>
            Notes
          </div>
        )}
      </div>
    </div>
  );
}

function KanbanColumn({
  status,
  label,
  jobs,
  onStatusChange,
  onDelete,
  onDrop,
}: {
  status: JobStatus;
  label: string;
  jobs: Job[];
  onStatusChange: (id: string, status: JobStatus) => void;
  onDelete: (id: string) => void;
  onDrop: (jobId: string, status: JobStatus) => void;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isRejected = status === 'REJECTED';
  const isOffer = status === 'OFFER';

  return (
    <div
      className={`flex min-h-0 shrink-0 flex-col gap-4 transition-all duration-200 ${
        collapsed ? 'w-14' : 'w-80 min-w-[280px]'
      } ${isDragOver ? 'opacity-80' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const jobId = e.dataTransfer.getData('jobId');
        if (jobId) onDrop(jobId, status);
      }}
    >
      {collapsed ? (
        /* Collapsed view — vertical pill */
        <div
          className={`flex flex-col items-center gap-3 rounded-xl border border-outline-variant/10 bg-surface-container px-2 py-4 h-full cursor-pointer select-none transition-colors hover:bg-surface-container-high ${
            isDragOver ? 'border-primary/40' : ''
          }`}
          onClick={() => setCollapsed(false)}
          title={`Expand ${label}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setCollapsed(false); }}
            className="text-on-surface-variant hover:text-on-surface transition-colors"
          >
            <span className="material-symbols-outlined text-base">chevron_right</span>
          </button>
          <div className="flex flex-col items-center gap-1" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            <span className={`font-semibold text-xs ${isRejected ? 'text-on-surface-variant' : 'text-on-surface'}`}>
              {label}
            </span>
          </div>
          <span
            className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
              isOffer
                ? 'bg-primary-container/20 text-primary'
                : 'bg-surface-container-highest text-on-surface-variant'
            }`}
          >
            {jobs.length}
          </span>
        </div>
      ) : (
        <>
          {/* Column header */}
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCollapsed(true)}
                className="text-on-surface-variant hover:text-on-surface transition-colors"
                title={`Collapse ${label}`}
              >
                <span className="material-symbols-outlined text-base">chevron_left</span>
              </button>
              <h3 className={`font-semibold text-sm ${isRejected ? 'text-on-surface-variant' : 'text-on-surface'}`}>
                {label}
              </h3>
              <span
                className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                  isOffer
                    ? 'bg-primary-container/20 text-primary'
                    : 'bg-surface-container-highest text-on-surface-variant'
                } ${status === 'HR' ? 'border border-primary/20' : ''}`}
              >
                {jobs.length}
              </span>
            </div>
          </div>

          {/* Cards */}
          <div className={`flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto no-scrollbar pr-1 ${isRejected ? 'opacity-60' : ''}`}>
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} onStatusChange={onStatusChange} onDelete={onDelete} />
            ))}
            {jobs.length === 0 && (
              <div
                className={`h-20 rounded-xl border-2 border-dashed ${
                  isDragOver ? 'border-primary/40' : 'border-outline-variant/15'
                } flex items-center justify-center transition-colors`}
              >
                <p className="text-xs text-on-surface-variant">Drop here</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function JobTracker() {
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => jobsService.findAll(),
  });

  const { data: stats } = useQuery({
    queryKey: ['job-stats'],
    queryFn: () => jobsService.getStats(),
    initialData: { total: 0, interviews: 0, offers: 0, conversionRate: 0 },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: JobStatus }) =>
      jobsService.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-stats'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => jobsService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-stats'] });
    },
  });

  const handleDrop = (jobId: string, newStatus: JobStatus) => {
    const job = jobs.find((j) => j.id === jobId);
    if (job && job.status !== newStatus) {
      updateMutation.mutate({ id: jobId, status: newStatus });
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const visibleJobs = normalizedSearch
    ? jobs.filter((job) =>
        [job.company, job.role, job.notes ?? '', job.jobUrl ?? ''].some((value) =>
          value.toLowerCase().includes(normalizedSearch),
        ),
      )
    : jobs;

  const jobsByStatus = (status: JobStatus) => visibleJobs.filter((j) => j.status === status);

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* Kanban section */}
      <section className="flex-1 min-h-0 overflow-hidden bg-surface">
        <div className="mx-auto flex h-full w-full max-w-7xl flex-col overflow-hidden px-4 py-6 sm:px-6 sm:py-8 lg:p-12">
          {/* Page header */}
          <div className="mb-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-center md:mb-8">
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Job Tracker</p>
              <h2 className="text-2xl font-semibold tracking-tight text-on-surface">Application Pipeline</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Manage your active job opportunities and interview stages.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <label className="flex w-full items-center gap-2 rounded-md border border-outline-variant/10 bg-surface-container-high px-3 py-2 text-sm text-on-surface transition-colors focus-within:border-primary/30 sm:min-w-[280px]">
                <span className="material-symbols-outlined text-base text-on-surface-variant">search</span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search company, role, notes or URL"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-on-surface-variant"
                />
              </label>
              <button
                data-tour="tour-add-job"
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md primary-gradient px-4 py-2 text-sm font-bold text-on-primary transition-transform active:scale-95"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                New Application
              </button>
            </div>
          </div>

          {/* Board */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden pb-4">
              <div className="flex h-full min-w-max gap-6">
                {COLUMNS.map((col) => (
                  <KanbanColumn
                    key={col.status}
                    status={col.status}
                    label={col.label}
                    jobs={jobsByStatus(col.status)}
                    onStatusChange={(id, status) => updateMutation.mutate({ id, status })}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer stats bar */}
      <footer className="bg-surface-container-low border-t border-outline-variant/10 px-4 md:px-8 py-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-[10px] uppercase tracking-widest font-bold text-on-surface-variant shrink-0">
        <div className="flex items-center gap-4 md:gap-8">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span>{stats.offers} Active {stats.offers === 1 ? 'Offer' : 'Offers'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-tertiary" />
            <span>{stats.interviews} {stats.interviews === 1 ? 'Interview' : 'Interviews'} Scheduled</span>
          </div>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          <span>Success Rate: {stats.conversionRate}%</span>
          <span className="text-outline-variant hidden sm:inline">|</span>
          <span>{stats.total} Total</span>
        </div>
      </footer>

      {showModal && (
        <AddJobModal
          existingJobs={jobs}
          onClose={() => setShowModal(false)}
          onContinue={(jobDescription) => {
            setShowModal(false);
            navigate('/assistant', {
              state: {
                initialMessage: jobDescription,
                autoCreateJob: true,
              },
            });
          }}
        />
      )}
    </div>
  );
}
